import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { BlueLogo, WhiteLogo } from '../../assets/images';
import attachLandingBehavior from './landing-behavior';
import LandingIcons from './landing-icons';
import './landing.css';

const PAGE_TITLE = 'Grigo: the AI hub for multiple LLM providers';

interface TokenPayload {
  exp?: number;
}

function authenticatedHome(): string | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = jwtDecode<TokenPayload>(JSON.parse(token));
    const expired = (decoded?.exp ?? 0) * 1000 < Date.now();
    if (expired) return null;
    const raw = localStorage.getItem('user_details');
    const role = raw ? String(JSON.parse(raw)?.role ?? '').toLowerCase() : '';
    if (role === 'admin' || role === 'super admin' || role === 'super-admin') return '/dashboard';
    return '/chat';
  } catch {
    return null;
  }
}

function Landing() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const logoSrc = useRef(
    window.matchMedia('(prefers-color-scheme: dark)').matches ? WhiteLogo : BlueLogo,
  ).current;
  const home = authenticatedHome();

  useEffect(() => {
    if (home) navigate(home, { replace: true });
  }, [home, navigate]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = document.documentElement;
    const previousScroll = root.style.scrollBehavior;
    if (!reduceMotion) root.style.scrollBehavior = 'smooth';
    return () => {
      document.title = previousTitle;
      root.style.scrollBehavior = previousScroll;
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || home) return undefined;
    return attachLandingBehavior(root, { light: BlueLogo, dark: WhiteLogo });
  }, [home]);

  if (home) return null;

  return (
    <div className="grigo-landing" ref={rootRef} id="top">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <LandingIcons />
      <header className="site-header" id="header">
        <div className="nav-pill">
          <a className="brand" href="#top" aria-label="GRIGO, back to top">
            <img src={logoSrc} alt="" />
          </a>
          <nav className="nav" id="nav" aria-label="Main">
            <a href="#features">Features</a>
            <a href="#cost">Cost Management</a>
            <a href="#control">Control</a>
            <a href="#knowledge">Knowledge</a>
            <Link className="nav-cta" to="/login">
              Try it
            </Link>
          </nav>
          <div className="header-actions">
            <button
              className="icon-btn"
              id="theme"
              type="button"
              aria-pressed="false"
              aria-label="Dark mode"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M9 2a7 7 0 0 1 0 14Z" fill="currentColor" />
              </svg>
            </button>
            <button
              className="icon-btn menu-btn"
              id="menu"
              type="button"
              aria-expanded="false"
              aria-controls="nav"
            >
              Menu
            </button>
            <Link className="btn btn-primary btn-sm" to="/login">
              Try it
            </Link>
          </div>
        </div>
      </header>

      <main id="main">
        <section className="hero" aria-labelledby="h1">
          <div className="aurora" aria-hidden="true">
            <i className="a1" />
            <i className="a2" />
            <i className="a3" />
          </div>
          <div className="wrap">
            <div className="hero-grid">
              <div className="hero-copy">
                <h1 id="h1" className="rise" style={{ animationDelay: '0ms' }}>
                  One place for chat, agents, and every app.
                </h1>
                <p className="lead rise" style={{ animationDelay: '90ms' }}>
                  Grigo is a centralized hub for integrating AI tools like ChatGPT, Claude, and
                  Gemini. It facilitates effortless interaction with Large Language Models,
                  simplifying performance comparison, enhancing operational efficiency, and
                  supporting effective prompt engineering.
                </p>
                <div className="btn-row rise" style={{ animationDelay: '180ms' }}>
                  <Link className="btn btn-primary" to="/login">
                    Try it
                  </Link>
                </div>
                <dl className="stats rise" style={{ animationDelay: '360ms' }}>
                  <div>
                    <dt>Files, web search, and history</dt>
                    <dd>Chat</dd>
                  </div>
                  <div>
                    <dt>One key for every app</dt>
                    <dd>Gateway</dd>
                  </div>
                  <div>
                    <dt>Alerts, caps, and audit</dt>
                    <dd>Budgets</dd>
                  </div>
                </dl>
              </div>

              <div
                className="hub anim rise"
                style={{ animationDelay: '180ms' }}
                role="img"
                aria-label="Chat, agents and apps connect to Grigo, which connects to OpenAI, Anthropic, Google and other LLM providers."
              >
                <div className="hub-top" aria-hidden="true">
                  <span className="live">Grigo AI hub</span>
                </div>
                <div className="hub-stage" aria-hidden="true">
                  <ul className="hub-col hub-in">
                    <li className="hn">
                      <svg className="ic">
                        <use href="#i-users" />
                      </svg>
                      <span>
                        People<small>Chat</small>
                      </span>
                    </li>
                    <li className="hn">
                      <svg className="ic">
                        <use href="#i-key" />
                      </svg>
                      <span>
                        Apps<small>Gateway keys</small>
                      </span>
                    </li>
                    <li className="hn">
                      <svg className="ic">
                        <use href="#i-sparkle" />
                      </svg>
                      <span>
                        Agents<small>Assistants</small>
                      </span>
                    </li>
                  </ul>
                  <svg className="hub-lines" viewBox="0 0 100 300" preserveAspectRatio="none">
                    <path className="base" d="M0 50 C55 50 45 150 100 150" />
                    <path className="base" d="M0 150 H100" />
                    <path className="base" d="M0 250 C55 250 45 150 100 150" />
                    <path className="flow" d="M0 50 C55 50 45 150 100 150" />
                    <path className="flow" d="M0 150 H100" />
                    <path className="flow" d="M0 250 C55 250 45 150 100 150" />
                  </svg>
                  <div className="hub-core">
                    <span className="ring r1" />
                    <span className="ring r2" />
                    <img src={WhiteLogo} alt="" />
                  </div>
                  <svg className="hub-lines" viewBox="0 0 100 300" preserveAspectRatio="none">
                    <path className="base" d="M0 150 C55 150 45 37.5 100 37.5" />
                    <path className="base" d="M0 150 C55 150 45 112.5 100 112.5" />
                    <path className="base" d="M0 150 C55 150 45 187.5 100 187.5" />
                    <path className="base" d="M0 150 C55 150 45 262.5 100 262.5" />
                    <path className="flow" d="M0 150 C55 150 45 37.5 100 37.5" />
                    <path className="flow" d="M0 150 C55 150 45 112.5 100 112.5" />
                    <path className="flow" d="M0 150 C55 150 45 187.5 100 187.5" />
                    <path className="flow" d="M0 150 C55 150 45 262.5 100 262.5" />
                  </svg>
                  <ul className="hub-col hub-out">
                    <li className="hn">
                      <svg className="pm pm-openai">
                        <use href="#pm-openai" />
                      </svg>
                      <span>OpenAI</span>
                    </li>
                    <li className="hn">
                      <svg className="pm pm-anthropic">
                        <use href="#pm-anthropic" />
                      </svg>
                      <span>Anthropic</span>
                    </li>
                    <li className="hn">
                      <svg className="pm pm-google">
                        <use href="#pm-google" />
                      </svg>
                      <span>Google</span>
                    </li>
                    <li className="hn more">
                      <svg className="ic ic-sm">
                        <use href="#i-plus" />
                      </svg>
                      <span>More providers</span>
                    </li>
                  </ul>
                </div>
                <ul className="hub-feats" aria-hidden="true">
                  <li className="tag">Routing</li>
                  <li className="tag">Budgets</li>
                  <li className="tag">Masking</li>
                  <li className="tag">Knowledge</li>
                  <li className="tag">Audit</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section
          className="section"
          id="features"
          aria-labelledby="features-h"
          style={{ paddingTop: 'clamp(24px,3vw,40px)' }}
        >
          <div className="wrap">
            <div className="center-head">
              <h2 id="features-h">What you get in Grigo.</h2>
            </div>
            <div className="feat-grid">
              <article className="feat">
                <span className="ico ico--blue">
                  <svg className="ic ic-lg">
                    <use href="#i-msg" />
                  </svg>
                </span>
                <h3>Enterprise chat</h3>
                <p>
                  One conversation across models, with files, web search, reasoning, and
                  spreadsheets.
                </p>
              </article>
              <article className="feat">
                <span className="ico ico--sage">
                  <svg className="ic ic-lg">
                    <use href="#i-columns" />
                  </svg>
                </span>
                <h3>Playground</h3>
                <p>Send one prompt to more than one model and keep the answer you want.</p>
              </article>
              <article className="feat">
                <span className="ico ico--amber">
                  <svg className="ic ic-lg">
                    <use href="#i-sparkle" />
                  </svg>
                </span>
                <h3>Agents and assistants</h3>
                <p>Shared personas and optimized prompts the whole company can reuse.</p>
              </article>
              <article className="feat">
                <span className="ico ico--blue">
                  <svg className="ic ic-lg">
                    <use href="#i-db" />
                  </svg>
                </span>
                <h3>Knowledge bases</h3>
                <p>
                  Answers grounded in uploaded files, Google Drive, or S3. Use RAG to deliver
                  contextual responses
                </p>
              </article>
              <article className="feat">
                <span className="ico ico--sage">
                  <svg className="ic ic-lg">
                    <use href="#i-key" />
                  </svg>
                </span>
                <h3>AI Gateway</h3>
                <p>
                  One key for OpenAI-style and Gemini-style apps, with failover, a budget, and IP
                  limits.
                </p>
              </article>
              <article className="feat">
                <span className="ico ico--amber">
                  <svg className="ic ic-lg">
                    <use href="#i-shield" />
                  </svg>
                </span>
                <h3>PII Detection</h3>
                <p>
                  Personal data is masked in text, spreadsheets, and images before it reaches the
                  model. Custom patterns and sensitive keywords included.
                </p>
              </article>
              <article className="feat">
                <span className="ico ico--blue">
                  <svg className="ic ic-lg">
                    <use href="#i-coin" />
                  </svg>
                </span>
                <h3>Cost and governance</h3>
                <p>
                  The cheapest capable model, per-user quotas, team budgets, and usage and audit
                  dashboards.
                </p>
              </article>
              <article className="feat">
                <span className="ico ico--sage">
                  <svg className="ic ic-lg">
                    <use href="#i-layers" />
                  </svg>
                </span>
                <h3>Semantic cache</h3>
                <p>A question that means the same thing reuses the earlier answer.</p>
              </article>
              <article className="feat">
                <span className="ico ico--amber">
                  <svg className="ic ic-lg">
                    <use href="#i-zap" />
                  </svg>
                </span>
                <h3>Integrations</h3>
                <p>MCP tools, embeddings, and your own provider keys.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section" id="cost" aria-labelledby="cost-h" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="center-head">
              <h2 id="cost-h">Spend less on the models that can do the job.</h2>
              <p className="lead">
                Auto model routing, more than one provider, and a cap on every user, team, and
                gateway key.
              </p>
            </div>
            <div className="cost-grid">
              <div className="levers">
                <article className="feat">
                  <span className="ico ico--sage ico--sm">
                    <svg className="ic">
                      <use href="#i-route" />
                    </svg>
                  </span>
                  <h3>Auto model routing</h3>
                  <p>Simple requests go to the cheapest model that can handle them.</p>
                </article>
                <article className="feat">
                  <span className="ico ico--blue ico--sm">
                    <svg className="ic">
                      <use href="#i-columns" />
                    </svg>
                  </span>
                  <h3>More than one provider</h3>
                  <p>Multiple providers, and the models people pick in chat and the playground.</p>
                </article>
                <article className="feat">
                  <span className="ico ico--amber ico--sm">
                    <svg className="ic">
                      <use href="#i-coin" />
                    </svg>
                  </span>
                  <h3>Budget alerts and caps</h3>
                  <p>A monthly budget per user, per team, and per gateway key.</p>
                </article>
                <article className="feat">
                  <span className="ico ico--sage ico--sm">
                    <svg className="ic">
                      <use href="#i-chart" />
                    </svg>
                  </span>
                  <h3>Cost-aware routing</h3>
                  <p>At the cap, the next request can stop or move to a cheaper model.</p>
                </article>
              </div>
              <div className="card" aria-labelledby="spend-h">
                <div className="card-head">
                  <h3 id="spend-h">
                    <span className="ico ico--blue ico--sm">
                      <svg className="ic">
                        <use href="#i-coin" />
                      </svg>
                    </span>
                    Spend by team
                  </h3>
                </div>
                <div className="kpis">
                  <div className="kpi">
                    <b>$1,307</b>
                    <span>Spent this month</span>
                  </div>
                  <div className="kpi">
                    <b>$693</b>
                    <span>Budget left</span>
                  </div>
                  <div className="kpi">
                    <b>2</b>
                    <span>Alerts open</span>
                  </div>
                </div>
                <div className="brow">
                  <span>Engineering</span>
                  <div
                    className="meter"
                    role="img"
                    aria-label="Engineering: 62 percent of budget used"
                  >
                    <i style={{ width: '62%' }} />
                  </div>
                  <span className="amt">$620 / $1,000</span>
                </div>
                <div className="brow">
                  <span>Support</span>
                  <div className="meter" role="img" aria-label="Support: 91 percent of budget used">
                    <i className="over" style={{ width: '91%' }} />
                  </div>
                  <span className="amt">$455 / $500</span>
                </div>
                <div className="brow">
                  <span>Finance</span>
                  <div className="meter" role="img" aria-label="Finance: 28 percent of budget used">
                    <i style={{ width: '28%' }} />
                  </div>
                  <span className="amt">$84 / $300</span>
                </div>
                <div className="brow">
                  <span>Gateway apps</span>
                  <div
                    className="meter"
                    role="img"
                    aria-label="Gateway apps: 74 percent of budget used"
                  >
                    <i className="warn" style={{ width: '74%' }} />
                  </div>
                  <span className="amt">$148 / $200</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="try" aria-labelledby="try-h" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="center-head">
              <h2 id="try-h">Masked before any model sees it.</h2>
              <p className="lead">
                Type a message. Grigo shows what it would mask, for every model in the chat. Nothing
                on this page leaves your browser.
              </p>
            </div>
            <section className="window-wrap" aria-label="Privacy check demo">
              <div className="window">
                <div className="win-main">
                  <div className="win-head">
                    <span className="win-title">Try it: type a message</span>
                    <span className="chip-static" aria-hidden="true">
                      <svg className="ic ic-sm">
                        <use href="#i-shield" />
                      </svg>
                      Only you
                      <i className="sep" />
                      Any model
                      <i className="sep" />
                      Masked first
                    </span>
                  </div>
                  <div className="composer">
                    <div className="field">
                      <div className="hl" id="demo-hl" aria-hidden="true" />
                      <textarea
                        id="demo-input"
                        maxLength={320}
                        spellCheck={false}
                        aria-label="Your message"
                      />
                    </div>
                    <div className="composer-bar">
                      <svg className="ic" aria-hidden="true">
                        <use href="#i-clip" />
                      </svg>
                      <span className="chip-static" aria-hidden="true">
                        <svg className="ic ic-sm">
                          <use href="#i-sparkle" />
                        </svg>
                        Auto
                        <svg className="ic ic-sm">
                          <use href="#i-down" />
                        </svg>
                      </span>
                      <span className="chip-static" aria-hidden="true">
                        <svg className="ic ic-sm">
                          <use href="#i-columns" />
                        </svg>
                        Compare
                      </span>
                      <span className="grow" />
                      <button className="send" id="demo-send" type="button" aria-label="Send">
                        <svg className="ic">
                          <use href="#i-up" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="pii-bar">
                    <div className="banner" id="demo-banner" />
                    <p className="demo-msg" id="demo-msg" />
                  </div>
                  <div className="seen">
                    <h3>What the model sees</h3>
                    <p className="seen-text" id="demo-seen" />
                    <p className="small muted" id="demo-restore" hidden>
                      The answer comes back with the original value restored, for you only.
                    </p>
                  </div>
                </div>
                <aside className="panel" aria-labelledby="demo-title">
                  <p className="panel-title" id="demo-title">
                    <span className="ico ico--blue ico--sm">
                      <svg className="ic">
                        <use href="#i-shield" />
                      </svg>
                    </span>
                    Privacy check
                  </p>
                  <div className="demo-presets" role="group" aria-label="Try an example">
                    <button className="chip" type="button" data-preset="mixed">
                      Email, phone and card
                    </button>
                    <button className="chip" type="button" data-preset="key">
                      An API key
                    </button>
                    <button className="chip" type="button" data-preset="clean">
                      No personal data
                    </button>
                  </div>
                  <ul className="findings" id="demo-findings" aria-label="What was found" />
                  <p className="demo-foot">
                    Grigo detects all kinds of personal PIIs like emails, phone numbers, card
                    numbers, API keys and more. Masking covers text, spreadsheets, and images,
                    including patterns you add and sensitive keywords.
                  </p>
                </aside>
              </div>
            </section>
          </div>
        </section>

        <section
          className="section"
          id="control"
          aria-labelledby="control-h"
          style={{ paddingTop: 0 }}
        >
          <div className="wrap">
            <div className="center-head">
              <h2 id="control-h">See who spent what, and who is allowed to.</h2>
              <p className="lead">
                Users, roles, and teams each get models and a budget. Dashboards and the audit trail
                show what happened.
              </p>
            </div>
            <div className="story-grid">
              <article className="card">
                <div className="card-head">
                  <h3>
                    <span className="ico ico--amber ico--sm">
                      <svg className="ic">
                        <use href="#i-lock" />
                      </svg>
                    </span>
                    Who can use what
                  </h3>
                </div>
                <ul className="story-list">
                  <li>
                    <strong>Users, roles, and teams</strong>
                    <span>Access follows the role and the team, not a shared login.</span>
                  </li>
                  <li>
                    <strong>Models they may use</strong>
                    <span>Each person or team is limited to the providers you assign.</span>
                  </li>
                  <li>
                    <strong>A budget on the same record</strong>
                    <span>The cap sits with the user, the team, or the gateway key.</span>
                  </li>
                </ul>
              </article>
              <article className="card">
                <div className="card-head">
                  <h3>
                    <span className="ico ico--blue ico--sm">
                      <svg className="ic">
                        <use href="#i-chart" />
                      </svg>
                    </span>
                    What you can see
                  </h3>
                </div>
                <ul className="story-list">
                  <li>
                    <strong>Usage dashboard</strong>
                    <span>Spend and requests by provider, team, and gateway key.</span>
                  </li>
                  <li>
                    <strong>Sensitive keywords</strong>
                    <span>Track the terms you mark, alongside masked personal data.</span>
                  </li>
                  <li>
                    <strong>Audit trail</strong>
                    <span>Settings changes and key actions, with who made them.</span>
                  </li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section
          className="section"
          id="knowledge"
          aria-labelledby="knowledge-h"
          style={{ paddingTop: 0 }}
        >
          <div className="wrap">
            <div className="center-head">
              <h2 id="knowledge-h">The same assistant, in chat and on the gateway.</h2>
              <p className="lead">
                A person asks in chat, or an app calls the gateway. A shared assistant shapes the
                prompt. If a knowledge base is attached, the answer is grounded in your files. That
                retrieval is how Grigo does RAG. Masking and the budget apply on both paths.
              </p>
            </div>
            <div className="flow-steps">
              <article className="flow-step">
                <b>Step 1</b>
                <h3>Ask</h3>
                <p>From chat, the playground, or a gateway key.</p>
              </article>
              <article className="flow-step">
                <b>Step 2</b>
                <h3>Use an assistant</h3>
                <p>A shared persona and an optimized prompt the company reuses.</p>
              </article>
              <article className="flow-step">
                <b>Step 3</b>
                <h3>Ground the answer</h3>
                <p>Retrieval from uploaded files, Google Drive, or S3.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section" id="specs" aria-labelledby="specs-h" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="center-head">
              <h2 id="specs-h">Specifications.</h2>
            </div>
            <div className="specs-card">
              <dl className="specs">
                <div className="spec">
                  <dt>Chat</dt>
                  <dd>
                    Files, web search, reasoning, and spreadsheets. The playground compares models
                    on one prompt.
                  </dd>
                </div>
                <div className="spec">
                  <dt>Routing</dt>
                  <dd>
                    The cheapest model that can handle the request, then the semantic cache when the
                    question means the same thing.
                  </dd>
                </div>
                <div className="spec">
                  <dt>Budgets</dt>
                  <dd>Per user, team, and gateway key, with alerts and caps.</dd>
                </div>
                <div className="spec">
                  <dt>Privacy</dt>
                  <dd>
                    Masking in text, spreadsheets, and images. Custom patterns and sensitive
                    keywords.
                  </dd>
                </div>
                <div className="spec">
                  <dt>Knowledge</dt>
                  <dd>Uploads, Google Drive, or S3, from chat and from the gateway.</dd>
                </div>
                <div className="spec">
                  <dt>Gateway</dt>
                  <dd>One key, failover, a budget, and IP limits. Bring your own provider keys.</dd>
                </div>
                <div className="spec">
                  <dt>Access</dt>
                  <dd>Users, roles, teams, dashboards, and an audit trail.</dd>
                </div>
                <div className="spec">
                  <dt>Integrations</dt>
                  <dd>MCP tools, embeddings.</dd>
                </div>
                <div className="spec">
                  <dt>Sign-in</dt>
                  <dd>Single sign-on for the company account.</dd>
                </div>
                <div className="spec">
                  <dt>Audit export</dt>
                  <dd>The audit trail can be exported.</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section className="section" aria-labelledby="close-h" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <h2 id="close-h">Chat, the gateway, and the budgets in one place.</h2>
              <div className="btn-row">
                <Link className="btn btn-primary" to="/login">
                  Try it
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="wrap footer-row">
          <div className="footer-id">
            <a className="brand" href="#top" aria-label="GRIGO, back to top">
              <img src={logoSrc} alt="" />
            </a>
            <p className="footer-note">© 2026 Grigo.</p>
          </div>
          <nav aria-label="Footer">
            <ul>
              <li>
                <a href="#features">Features</a>
              </li>
              <li>
                <a href="#cost">Cost</a>
              </li>
              <li>
                <a href="#control">Control</a>
              </li>
              <li>
                <a href="#knowledge">Knowledge</a>
              </li>
              <li>
                <a href="#try">Privacy demo</a>
              </li>
              <li>
                <a href="#specs">Specs</a>
              </li>
              <li>
                <Link to="/login">Try it</Link>
              </li>
            </ul>
          </nav>
        </div>
      </footer>

      <div id="live" className="sr-only" aria-live="polite" role="status" />
    </div>
  );
}

export default Landing;
